import { useEffect, useState } from "react";
import { Loader2, Building2, ShieldAlert, GraduationCap, Clock, TriangleAlert, Users, TrendingUp, Info } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { StatCard } from "../components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const RAG = {
  green: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/25",
  amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/25",
  red: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/25",
};
const BAR = { green: "#10b981", amber: "#f59e0b", red: "#ef4444" };

const METRICS = [
  { key: "safety", label: "Safety score", rag: "safety_rag", max: 100 },
  { key: "training", label: "Training %", rag: "training_rag", max: 100 },
  { key: "overdue", label: "Overdue actions", rag: "overdue_rag" },
  { key: "high_risks", label: "Open high risks", rag: "high_risks_rag" },
  { key: "reports_per_100", label: "Reports / 100 staff", rag: "reporting_rag" },
];

const Cell1 = ({ rag, children, testid }) => (
  <td className="px-3 py-3" data-testid={testid}>
    <span className={`inline-flex min-w-[3rem] items-center justify-center rounded-lg border px-2.5 py-1 text-sm font-semibold ${RAG[rag]}`}>{children}</span>
  </td>
);

export default function GroupOperations() {
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState(METRICS[0]);
  const [sel, setSel] = useState(null);

  useEffect(() => { api.get("/group/operations").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;
  const { group, sites } = data;
  const chartData = [...sites].map((s) => ({ name: s.name.split(" ").slice(0, 2).join(" "), value: s[metric.key], rag: s[metric.rag] }));

  return (
    <div>
      <PageHeader title="Group Operations View" subtitle={`Live performance across ${group.sites} sites · ${group.headcount.toLocaleString()} people. Colours flag where to focus first.`} />

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard icon={ShieldAlert} label="Avg safety score" value={group.avg_safety} tone="bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]" />
        <StatCard icon={GraduationCap} label="Avg training" value={`${group.avg_training}%`} tone="bg-blue-500/10 text-blue-600 dark:text-blue-400" />
        <StatCard icon={Clock} label="Overdue actions" value={group.overdue_actions} tone="bg-amber-500/10 text-amber-600 dark:text-amber-400" />
        <StatCard icon={TriangleAlert} label="Open high risks" value={group.high_risks} tone="bg-red-500/10 text-red-600 dark:text-red-400" />
        <StatCard icon={Building2} label="Sites needing focus" value={group.at_risk_sites} tone="bg-red-500/10 text-red-600 dark:text-red-400" />
      </div>

      {/* Comparison chart */}
      <Card className="mb-6 rounded-2xl border-border p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="font-heading text-base font-semibold">Site comparison</div>
          <div className="flex flex-wrap gap-1.5" data-testid="group-metric-switch">
            {METRICS.map((m) => (
              <button key={m.key} onClick={() => setMetric(m)} data-testid={`metric-${m.key}`}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${metric.key === m.key ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]" : "border-border text-muted-foreground hover:bg-secondary/60"}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-18} textAnchor="end" height={54} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" domain={metric.max ? [0, metric.max] : [0, "auto"]} />
              <Tooltip cursor={{ fill: "hsl(var(--secondary))" }} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {chartData.map((d, i) => <Cell key={i} fill={BAR[d.rag]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Traffic-light table */}
      <Card className="overflow-hidden rounded-2xl border-border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="group-rag-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-medium">Site</th>
                <th className="px-3 py-3 font-medium">Safety</th>
                <th className="px-3 py-3 font-medium">Training</th>
                <th className="px-3 py-3 font-medium">Actions overdue</th>
                <th className="px-3 py-3 font-medium">Open high risks</th>
                <th className="px-3 py-3 font-medium">Reporting rate</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((s) => (
                <tr key={s.id} onClick={() => setSel(s)} data-testid={`group-row-${s.id}`}
                  className="cursor-pointer border-b border-border/60 transition-colors last:border-0 hover:bg-secondary/50">
                  <td className="px-4 py-3">
                    <div className="font-medium">{s.name}</div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground"><Users className="h-3 w-3" />{s.headcount} staff</div>
                  </td>
                  <Cell1 rag={s.safety_rag} testid={`safety-${s.id}`}>{s.safety}</Cell1>
                  <Cell1 rag={s.training_rag} testid={`training-${s.id}`}>{s.training}%</Cell1>
                  <Cell1 rag={s.overdue_rag} testid={`overdue-${s.id}`}>{s.overdue}</Cell1>
                  <Cell1 rag={s.high_risks_rag} testid={`highrisk-${s.id}`}>{s.high_risks}</Cell1>
                  <Cell1 rag={s.reporting_rag} testid={`reporting-${s.id}`}>{s.reporting_rate}</Cell1>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-4 flex items-start gap-2 rounded-xl border border-border bg-secondary/40 p-4 text-xs text-muted-foreground" data-testid="group-caveat">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--accent))]" />
        <div>
          <span className="font-semibold text-foreground">Read reporting rate as engagement, not risk.</span> A site reporting more hazards often has a
          <em> stronger</em> safety culture. A <span className="font-medium text-red-600 dark:text-red-400">Low</span> reporting rate is a warning that issues may be going unseen —
          judge risk from Safety, Overdue and Open high risks; judge culture from Reporting rate.
        </div>
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{sel.name}</DialogTitle></DialogHeader>
              <DialogDescription className="sr-only">Site performance breakdown across risk, closure and engagement metrics.</DialogDescription>
              <div className="text-xs text-muted-foreground">{sel.location} · {sel.headcount} staff</div>

              <div className="mt-3 grid grid-cols-2 gap-3">
                <Lens title="Risk & closure">
                  <Row label="Safety score" rag={sel.safety_rag}>{sel.safety}</Row>
                  <Row label="Overdue actions" rag={sel.overdue_rag}>{sel.overdue}</Row>
                  <Row label="Open high risks" rag={sel.high_risks_rag}>{sel.high_risks}</Row>
                  <Row label="Open reports" rag="amber">{sel.open_reports}</Row>
                </Lens>
                <Lens title="Engagement & reporting">
                  <Row label="Reporting rate" rag={sel.reporting_rag}>{sel.reporting_rate}</Row>
                  <Row label="Reports (90d)" rag="green">{sel.reports_90d}</Row>
                  <Row label="Reports / 100 staff" rag="green">{sel.reports_per_100}</Row>
                  <Row label="Positive : negative" rag="green">{sel.pos_neg_ratio}</Row>
                </Lens>
              </div>

              <div className="mt-4">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><TrendingUp className="h-3.5 w-3.5" /> Recurring hazards</div>
                {sel.recurring.length ? (
                  <div className="flex flex-wrap gap-2">
                    {sel.recurring.map((r) => <Badge key={r.category} className="rounded-full bg-red-500/10 text-red-600 dark:text-red-400">{r.category} ×{r.count}</Badge>)}
                  </div>
                ) : <div className="text-sm text-muted-foreground">No category recurring 3+ times.</div>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Lens = ({ title, children }) => (
  <div className="rounded-xl border border-border p-3">
    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
    <div className="space-y-1.5">{children}</div>
  </div>
);
const Row = ({ label, rag, children }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={`inline-flex min-w-[2.5rem] items-center justify-center rounded-md border px-2 py-0.5 text-xs font-semibold ${RAG[rag]}`}>{children}</span>
  </div>
);
